import { StreamActions, session, visit } from "@hotwired/turbo"

// Custom Turbo Stream action to close the modal
StreamActions.close_modal = function() {
  // Dispatch event that modal controller listens to
  window.dispatchEvent(new CustomEvent('modal:close-requested'))
}

// Custom Turbo Stream action to close the slideover
StreamActions.close_slideover = function() {
  // Dispatch event that slideover controller listens to
  window.dispatchEvent(new CustomEvent('slideover:close-requested'))
}

// Custom Turbo Stream action to navigate the whole page.
// Usage: <turbo-stream action="visit" src="/path"></turbo-stream>
// For the cases a frame refresh can't cover — renaming a record regenerates
// its slug, so the page the form was opened from is now at a dead URL and
// patching frames would leave the browser one reload away from a 404.
StreamActions.visit = function() {
  const src = this.getAttribute("src")
  if (src) visit(src)
}

// Custom Turbo Stream action to navigate.
// Usage: <turbo-stream action="redirect_to" target="/path"></turbo-stream>
//
// Write the tag literally, as above. Seven templates reached for
// `turbo_stream.redirect_to some_path` instead — the site-level location
// refresh, both nested-location streams, and every block/floor archive and
// delete — and Turbo::Streams::TagBuilder has no such method and no
// method_missing, so every one of them raised NoMethodError and answered 500.
// Archiving a block from the site pages has been a server error, not a silent
// no-op: the toast never appeared either. Found covering the structure page's
// refresh paths (TASK-191.4); the action itself was registered a wave earlier
// (TASK-191.2), when the templates were read as emitting a tag Turbo dropped.
StreamActions.redirect_to = function() {
  const target = this.getAttribute("target")
  if (target) visit(target)
}

// Custom Turbo Stream action to open the page's modal from a response that
// has no click to hang `modal#open` off of.
// Usage: <turbo-stream action="open_modal" src="/path" modal-title="Title" modal-size="modal-sm"></turbo-stream>
//
// Born on the Planner's drag-to-reschedule/reassign (TASK-238.3): a card
// drop sends a turbo-stream-only fetch, and a series member whose scope is
// still ambiguous needs to ask before the drop applies — the ONLY way back
// to the user is the stream itself. Opening a modal everywhere else in this
// app is a real click on an element carrying `data-modal-turbo-frame-src-
// value` etc. (modal_controller.js#open reads them off `event.target`), but
// a synthetic click on a FRESHLY created, off-DOM element does not work
// here: Stimulus binds `data-action` through its own MutationObserver,
// which runs asynchronously, so a click dispatched in the same tick the
// element was appended fires before Stimulus has attached anything (found
// chasing exactly that race — the dialog silently never opened). Going
// straight at the already-connected controller instance (`window.Stimulus`,
// set in controllers/application.js) sidesteps the race entirely.
StreamActions.open_modal = function() {
  const src = this.getAttribute("src")
  if (!src) return

  const modalElement = document.querySelector('[data-controller~="modal"]')
  const controller = modalElement && window.Stimulus.getControllerForElementAndIdentifier(modalElement, "modal")
  if (!controller) return

  controller.turboFrameSrcValue = src
  controller.open()

  const title = this.getAttribute("modal-title")
  if (title) controller.applyModalTitle(title)
  const size = this.getAttribute("modal-size")
  if (size) controller.applyModalSize(size)
}

// Custom Turbo Stream action to refresh a turbo-frame's src.
// Usage: <turbo-stream action="refresh_frame" target="my-frame" src="/path"></turbo-stream>
StreamActions.refresh_frame = function() {
  const frame = document.getElementById(this.target)
  if (!frame) return
  const src = this.getAttribute("src") || frame.src
  frame.src = ""
  frame.src = src
}


// Custom Turbo Stream action to refresh the whole page at an explicit URL.
// Usage: <turbo-stream action="refresh_page" src="/dashboard"></turbo-stream>
//
// Turbo 8's own `refresh` action re-visits document.baseURI — but while a
// modal is open the modal controller has pushed the FRAME's url onto history
// (and only restores the page url 200ms into close), so a bare `refresh`
// streamed alongside close_modal re-visits the modal fragment as a page.
// Naming the url sidesteps that: session.refresh is the same replace-visit
// Turbo's own action performs, so a page declaring turbo-refresh-method
// morph still morphs (same pathname + replace = page refresh).
StreamActions.refresh_page = function() {
  const src = this.getAttribute("src")
  if (src) session.refresh(src)
}

// Custom Turbo Stream action to change the address bar without a visit.
// Usage: <turbo-stream action="replace_url" url="/forms/describe?generation=…"></turbo-stream>
//
// Born on the describe-a-form page (TASK-266.2): submitting a description
// swaps the page's regions in place by stream, and this puts the new
// generation in the URL so a reload comes back to it. Through Turbo's own
// history (same restoration id) so its idea of the current location — the
// key its snapshot cache uses — moves with the address bar.
StreamActions.replace_url = function() {
  const url = this.getAttribute("url")
  if (!url) return
  const location = new URL(url, window.location.href)
  if (session.history && typeof session.history.replace === "function") {
    session.history.replace(location, session.history.restorationIdentifier)
  } else {
    window.history.replaceState(window.history.state, "", location)
  }
}
